import React, { useState, useEffect, useRef } from 'react';
import { VscAdd, VscTrash, VscEdit, VscCheck, VscClose, VscChevronDown, VscChevronRight } from 'react-icons/vsc';
import { getUsers, createUser, updateUser, deleteUser, uploadUserAvatar } from '../../api/users';
import { getProjects } from '../../api/projects';
import Avatar from '../common/Avatar';
import { useSort, sortRows, SortTh } from '../common/SortHeader';
import './Users.css';

const ROLE_LABELS = {
  admin:         'Admin',
  engineer:      'Engineer',
  manager:       'Manager',
  foreman:       'Foreman',
  'team-leader': 'Team Leader',
  standard:      'Standard',
  client:        'Client',
  other:         'Other',
};

// Same ranking as backend/routes/users.js: Admin -> Manager -> Engineer -> Foreman -> Team Leader/Standard
// Client and Other share Foreman's rank, so Engineers and above manage them but Foremen don't
const RANK = { admin: 0, manager: 1, engineer: 2, foreman: 3, client: 3, other: 3, 'team-leader': 4, standard: 4 };
const USER_MANAGERS = ['admin', 'manager', 'engineer', 'foreman'];

// Admins manage everyone, other admins included; the rest only roles ranked strictly below their own
const canManageRole = (actorRole, targetRole) =>
  actorRole === 'admin' || (USER_MANAGERS.includes(actorRole) && RANK[targetRole] > RANK[actorRole]);

const CREATABLE = Object.fromEntries(USER_MANAGERS
  .map(r => [r, Object.keys(RANK).filter(t => canManageRole(r, t))]));

const NOTES_ROLES = ['admin', 'engineer', 'manager', 'foreman'];
const PROJECT_ROLES = ['team-leader', 'standard', 'client', 'other'];

const CONTACT_FIELDS = ['email', 'phone', 'emergencyContactName', 'emergencyContactPhone', 'emergencyContactRelation'];

const toTime = (d) => (d ? new Date(d).getTime() : null);

const getUserVal = (u, key) => {
  switch (key) {
    case 'displayName': return u.displayName || null;
    case 'role':        return ROLE_LABELS[u.role] || u.role;
    case 'dateOfBirth': return toTime(u.dateOfBirth);
    case 'expiresAt':   return toTime(u.expiresAt);
    case 'createdAt':   return toTime(u.createdAt);
    case 'contact':     return u.email || u.phone || null;
    case 'emergency':   return u.emergencyContactName || u.emergencyContactPhone || null;
    default:            return u[key] || null;
  }
};

const SEARCH_FIELDS = ['username', 'displayName', 'employeeNumber', 'email', 'phone'];

const emptyNew = (defaultRole) => ({
  username: '', password: '', displayName: '', employeeNumber: '', email: '', phone: '',
  role: defaultRole || 'standard', expiresAt: '', projects: [],
});

function ProjectChecklist({ allProjects, selected, onChange }) {
  const toggle = (id) => onChange(
    selected.includes(id) ? selected.filter(p => p !== id) : [...selected, id]
  );
  return (
    <div className="project-checklist">
      {allProjects.map(p => (
        <label key={p._id} className="project-check-item">
          <input type="checkbox" checked={selected.includes(p._id)} onChange={() => toggle(p._id)} />
          <span>{p.name}</span>
        </label>
      ))}
      {allProjects.length === 0 && <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>No projects yet.</span>}
    </div>
  );
}

function UserRow({ user, editing, currentRole, allProjects, onStartEdit, onSave, onCancel, onDelete, onAvatarUpload }) {
  const canEdit = canManageRole(currentRole, user.role);
  const canDelete = canEdit;
  const canSeeNotes = NOTES_ROLES.includes(currentRole);
  const creatableRoles = CREATABLE[currentRole] || [];
  const fileRef = useRef(null);

  if (editing) {
    return (
      <>
        <tr className="user-editing-row">
          <td>
            <div className="user-avatar-cell">
              <div className="user-avatar-wrap" onClick={() => fileRef.current?.click()} title="Change picture">
                <Avatar user={user} size={32} />
                <span className="user-avatar-edit-hint">✎</span>
              </div>
              <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
                onChange={e => { const f = e.target.files?.[0]; if (f) onAvatarUpload(user._id, f); e.target.value=''; }} />
            </div>
          </td>
          <td>{user.username}</td>
          <td>
            <input className="user-edit-input" value={editing.displayName || ''}
              placeholder="Full name"
              onChange={e => editing.set(p => ({ ...p, displayName: e.target.value }))} />
          </td>
          <td>
            <input className="user-edit-input" value={editing.employeeNumber || ''}
              placeholder="Emp. #"
              onChange={e => editing.set(p => ({ ...p, employeeNumber: e.target.value }))} />
          </td>
          <td>
            <input className="user-edit-input" type="date" value={editing.dateOfBirth}
              onChange={e => editing.set(p => ({ ...p, dateOfBirth: e.target.value }))} />
          </td>
          <td>
            <div className="user-edit-stack">
              <input className="user-edit-input" type="email" value={editing.email} placeholder="Email"
                onChange={e => editing.set(p => ({ ...p, email: e.target.value }))} />
              <input className="user-edit-input" type="tel" value={editing.phone} placeholder="Phone"
                onChange={e => editing.set(p => ({ ...p, phone: e.target.value }))} />
            </div>
          </td>
          <td>
            <div className="user-edit-stack">
              <input className="user-edit-input" value={editing.emergencyContactName} placeholder="Name"
                onChange={e => editing.set(p => ({ ...p, emergencyContactName: e.target.value }))} />
              <input className="user-edit-input" type="tel" value={editing.emergencyContactPhone} placeholder="Phone"
                onChange={e => editing.set(p => ({ ...p, emergencyContactPhone: e.target.value }))} />
              <input className="user-edit-input" value={editing.emergencyContactRelation} placeholder="Relation (e.g. spouse)"
                onChange={e => editing.set(p => ({ ...p, emergencyContactRelation: e.target.value }))} />
            </div>
          </td>
          <td>
            <select value={editing.role}
              onChange={e => editing.set(p => ({ ...p, role: e.target.value }))}>
              {creatableRoles.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
            </select>
          </td>
          <td>
            {editing.role === 'other'
              ? <input type="date" value={editing.expiresAt || ''} className="user-edit-input"
                  onChange={e => editing.set(p => ({ ...p, expiresAt: e.target.value }))} />
              : '—'}
          </td>
          <td>
            <input type="password" className="user-edit-input" placeholder="New password…"
              value={editing.password || ''}
              onChange={e => editing.set(p => ({ ...p, password: e.target.value }))} />
          </td>
          {canSeeNotes && (
            <td>
              <textarea className="user-edit-notes" value={editing.notes || ''}
                placeholder="Internal notes…"
                onChange={e => editing.set(p => ({ ...p, notes: e.target.value }))} />
            </td>
          )}
          <td>
            <div style={{ display: 'flex', gap: 4 }}>
              <button className="btn-icon" onClick={onSave} title="Save"><VscCheck size={14} /></button>
              <button className="btn-icon" onClick={onCancel} title="Cancel"><VscClose size={14} /></button>
            </div>
          </td>
        </tr>
        {PROJECT_ROLES.includes(editing.role) && (
          <tr>
            <td colSpan={canSeeNotes ? 12 : 11} style={{ background: 'var(--bg-tertiary)', padding: '8px 16px' }}>
              <ProjectChecklist allProjects={allProjects} selected={editing.projects}
                onChange={v => editing.set(p => ({ ...p, projects: v }))} />
            </td>
          </tr>
        )}
      </>
    );
  }

  return (
    <tr>
      <td>
        <Avatar user={user} size={28} />
      </td>
      <td>{user.username}</td>
      <td>{user.displayName || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
      <td>{user.employeeNumber || '—'}</td>
      <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
        {user.dateOfBirth ? new Date(user.dateOfBirth).toLocaleDateString(undefined, { timeZone: 'UTC' }) : '—'}
      </td>
      <td className="user-contact-cell">
        {user.email || user.phone ? (
          <>
            {user.email && <div><a href={`mailto:${user.email}`}>{user.email}</a></div>}
            {user.phone && <div><a href={`tel:${user.phone}`}>{user.phone}</a></div>}
          </>
        ) : '—'}
      </td>
      <td className="user-contact-cell">
        {user.emergencyContactName || user.emergencyContactPhone ? (
          <>
            <div>
              {user.emergencyContactName}
              {user.emergencyContactRelation && <span className="user-contact-muted"> ({user.emergencyContactRelation})</span>}
            </div>
            {user.emergencyContactPhone && <div><a href={`tel:${user.emergencyContactPhone}`}>{user.emergencyContactPhone}</a></div>}
          </>
        ) : '—'}
      </td>
      <td><span className={`role-badge role-${user.role}`}>{ROLE_LABELS[user.role] || user.role}</span></td>
      <td style={{ color: user.expiresAt && new Date() > new Date(user.expiresAt) ? 'var(--red)' : 'var(--text-secondary)', fontSize: 12 }}>
        {user.expiresAt ? new Date(user.expiresAt).toLocaleDateString() : '—'}
      </td>
      <td>{new Date(user.createdAt).toLocaleDateString()}</td>
      {canSeeNotes && (
        <td className="user-notes-cell">{user.notes || '—'}</td>
      )}
      <td>
        <div style={{ display: 'flex', gap: 4 }}>
          {canEdit && <button className="btn-icon" title="Edit" onClick={onStartEdit}><VscEdit size={14} /></button>}
          {canDelete && <button className="btn-icon" title="Delete" onClick={onDelete}><VscTrash size={14} /></button>}
        </div>
      </td>
    </tr>
  );
}

function UsersPanel({ role: currentRole }) {
  const [users, setUsers]       = useState([]);
  const [projects, setProjects] = useState([]);
  const [showNew, setShowNew]   = useState(false);
  const [editState, setEditState] = useState(null); // { _id, ...fields, set: fn }
  const [newForm, setNewForm]   = useState(() => emptyNew(CREATABLE[currentRole]?.[0] || 'standard'));
  const [showNewProjects, setShowNewProjects] = useState(false);
  const [error, setError]       = useState('');
  const [search, setSearch]     = useState('');
  const [sort, toggleSort]      = useSort('username');

  const creatableRoles = CREATABLE[currentRole] || [];
  const canSeeNotes = NOTES_ROLES.includes(currentRole);

  useEffect(() => {
    getUsers().then(r => setUsers(r.data)).catch(() => {});
    getProjects().then(r => setProjects(r.data)).catch(() => {});
  }, []);

  const handleAdd = async () => {
    if (!newForm.username.trim() || !newForm.password.trim())
      return setError('Username and password are required.');
    try {
      const res = await createUser({
        ...newForm,
        expiresAt: newForm.expiresAt || undefined,
      });
      setUsers(prev => [...prev, res.data]);
      setNewForm(emptyNew(creatableRoles[0]));
      setShowNew(false); setShowNewProjects(false); setError('');
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to create user.');
    }
  };

  const startEdit = (user) => {
    const state = {
      _id: user._id,
      role:           user.role,
      displayName:    user.displayName    || '',
      employeeNumber: user.employeeNumber || '',
      notes:          user.notes          || '',
      password:       '',
      expiresAt:      user.expiresAt ? new Date(user.expiresAt).toISOString().slice(0,10) : '',
      projects:       (user.projects || []).map(p => p._id || p),
    };
    CONTACT_FIELDS.forEach(f => { state[f] = user[f] || ''; });
    state.dateOfBirth = user.dateOfBirth ? new Date(user.dateOfBirth).toISOString().slice(0,10) : '';
    state.set = (fn) => setEditState(prev => ({ ...fn(prev), set: prev.set }));
    setEditState(state);
    setError('');
  };

  const handleSave = async () => {
    try {
      const payload = {
        role:           editState.role,
        displayName:    editState.displayName    || undefined,
        employeeNumber: editState.employeeNumber || undefined,
        notes:          editState.notes          || undefined,
        expiresAt:      editState.expiresAt      || null,
        projects:       editState.projects,
      };
      // Send empty strings so cleared contact fields are removed server-side
      CONTACT_FIELDS.forEach(f => { payload[f] = editState[f]; });
      payload.dateOfBirth = editState.dateOfBirth;
      if (editState.password) payload.password = editState.password;
      const res = await updateUser(editState._id, payload);
      setUsers(prev => prev.map(u => u._id === editState._id ? res.data : u));
      setEditState(null); setError('');
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to update user.');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this user?')) return;
    try {
      await deleteUser(id);
      setUsers(prev => prev.filter(u => u._id !== id));
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to delete user.');
    }
  };

  const handleAvatarUpload = async (userId, file) => {
    try {
      const form = new FormData();
      form.append('image', file);
      const res = await uploadUserAvatar(userId, form);
      setUsers(prev => prev.map(u => u._id === userId ? { ...u, profileImage: res.data.profileImage } : u));
    } catch {}
  };

  const colCount = canSeeNotes ? 12 : 11;

  const q = search.trim().toLowerCase();
  const visibleUsers = sortRows(
    q ? users.filter(u => SEARCH_FIELDS.some(f => u[f]?.toLowerCase().includes(q))) : users,
    sort, getUserVal
  );
  const sortProps = { sort, onSort: toggleSort };

  return (
    <div className="module-wrap">
      <div className="module-toolbar">
        <span className="module-toolbar-title">Users</span>
        <input className="module-search" placeholder="Search name…" value={search}
          onChange={e => setSearch(e.target.value)} />
        {creatableRoles.length > 0 && (
          <button className="btn-primary" onClick={() => { setShowNew(true); setError(''); }}>
            <VscAdd size={14} style={{ marginRight: 4 }} />New User
          </button>
        )}
      </div>
      <div className="module-body" style={{ padding: '16px 24px' }}>
        {error && <p style={{ color: 'var(--red)', fontSize: 12, marginBottom: 12 }}>{error}</p>}

        {showNew && (
          <div className="user-new-form">
            <div className="user-new-row">
              <input placeholder="Username *" value={newForm.username} autoFocus
                onChange={e => setNewForm(p => ({ ...p, username: e.target.value }))} />
              <input placeholder="Password *" type="password" value={newForm.password}
                onChange={e => setNewForm(p => ({ ...p, password: e.target.value }))} />
              <input placeholder="Display name" value={newForm.displayName}
                onChange={e => setNewForm(p => ({ ...p, displayName: e.target.value }))} />
              <input placeholder="Employee #" value={newForm.employeeNumber}
                onChange={e => setNewForm(p => ({ ...p, employeeNumber: e.target.value }))} />
              <input placeholder="Email" type="email" value={newForm.email}
                onChange={e => setNewForm(p => ({ ...p, email: e.target.value }))} />
              <input placeholder="Phone" type="tel" value={newForm.phone}
                onChange={e => setNewForm(p => ({ ...p, phone: e.target.value }))} />
              <select value={newForm.role} onChange={e => setNewForm(p => ({ ...p, role: e.target.value }))}>
                {creatableRoles.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
              </select>
              {newForm.role === 'other' && (
                <input type="date" title="Expiry date" value={newForm.expiresAt}
                  onChange={e => setNewForm(p => ({ ...p, expiresAt: e.target.value }))} />
              )}
            </div>
            {PROJECT_ROLES.includes(newForm.role) && (
              <div className="user-project-section">
                <button className="user-project-toggle" onClick={() => setShowNewProjects(v => !v)}>
                  {showNewProjects ? <VscChevronDown size={12} /> : <VscChevronRight size={12} />}
                  Project access ({newForm.projects.length} selected)
                </button>
                {showNewProjects && (
                  <ProjectChecklist allProjects={projects} selected={newForm.projects}
                    onChange={v => setNewForm(p => ({ ...p, projects: v }))} />
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button className="btn-primary" onClick={handleAdd}>Add</button>
              <button className="btn-secondary" onClick={() => { setShowNew(false); setShowNewProjects(false); }}>Cancel</button>
            </div>
          </div>
        )}

        <table className="data-table users-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}></th>
              <SortTh sortKey="username"       {...sortProps}>Username</SortTh>
              <SortTh sortKey="displayName"    {...sortProps}>Display Name</SortTh>
              <SortTh sortKey="employeeNumber" {...sortProps}>Emp. #</SortTh>
              <SortTh sortKey="dateOfBirth"    {...sortProps}>Date of Birth</SortTh>
              <SortTh sortKey="contact"        {...sortProps}>Contact</SortTh>
              <SortTh sortKey="emergency"      {...sortProps}>Emergency Contact</SortTh>
              <SortTh sortKey="role"           {...sortProps}>Role</SortTh>
              <SortTh sortKey="expiresAt"      {...sortProps}>Expires</SortTh>
              <SortTh sortKey="createdAt"      {...sortProps}>Created</SortTh>
              {canSeeNotes && <SortTh sortKey="notes" {...sortProps}>Notes</SortTh>}
              <th style={{ width: 72 }}></th>
            </tr>
          </thead>
          <tbody>
            {visibleUsers.map(user => (
              <UserRow
                key={user._id}
                user={user}
                editing={editState?._id === user._id ? editState : null}
                currentRole={currentRole}
                allProjects={projects}
                onStartEdit={() => startEdit(user)}
                onSave={handleSave}
                onCancel={() => setEditState(null)}
                onDelete={() => handleDelete(user._id)}
                onAvatarUpload={handleAvatarUpload}
              />
            ))}
            {visibleUsers.length === 0 && (
              <tr><td colSpan={colCount} style={{ color: 'var(--text-muted)', padding: '16px 10px' }}>
                {users.length === 0 ? 'No users yet.' : 'No users match the search.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default UsersPanel;
